import { ChangeDetectionStrategy, Component, effect, TemplateRef, ViewChild } from '@angular/core';
import {
  faFilePdf,
  faFileExcel,
  faFileText,
  faFileWord,
  faFileImage,
  faFileZipper,
} from '@fortawesome/free-regular-svg-icons';
import {
  faDesktop,
  faFileCircleQuestion,
  faCopy,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FilterMatchMode } from '@openng/optimus-ui/api';
import type { Table, TableRowExpandEvent } from '@openng/optimus-ui/table';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BackendService } from '@shared/backend.service';
import { SecureFileService } from '@shared/secure-file.service';
import { ProjectDetails } from '@shared/api_interfaces';
import { ProjectService } from '@app/shell/header/header.project.service';
import { PROJECT_FILES_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';

interface SampleColumn {
  title: string;
  data: string;
}

interface SampleRow extends Record<string, unknown> {
  sample_id: string;
}

interface ProjectFile {
  file_name: string;
  file_path: string;
  file_url: string;
  file_icon: IconDefinition;
}

interface SampleFile {
  fileName: string;
  filePath: string;
  fileIcon: IconDefinition;
}

interface SampleFileSection {
  title: string;
  depth: number;
  files: SampleFile[];
}

type SampleFilesState =
  { status: 'loading' } | { status: 'loaded'; sections: SampleFileSection[] } | { status: 'error' };

const iconLookup = {
  '.xlsx': faFileExcel,
  '.xls': faFileExcel,
  '.pdf': faFilePdf,
  '.txt': faFileText,
  '.docx': faFileWord,
  '.doc': faFileWord,
  '.html': faDesktop,
  '.png': faFileImage,
  '.fastq': faFileText,
  '.zip': faFileZipper,
  '.gz': faFileZipper,
  '.tsv': faFileExcel,
  '.tab': faFileExcel,
  '.csv': faFileExcel,
  '.vcf': faFileText,
  '.bam': faFileZipper,
  '.bai': faFileText,
  '.log': faFileText,
  '.stats': faFileText,
  '.flagstats': faFileText,
  '.fasta': faFileText,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSampleColumn(value: unknown): value is SampleColumn {
  return (
    isRecord(value) &&
    typeof value['title'] === 'string' &&
    typeof value['data'] === 'string' &&
    value['data'].length > 0
  );
}

function isSampleRow(value: unknown): value is SampleRow {
  return isRecord(value) && typeof value['sample_id'] === 'string';
}

@Component({
  selector: 'app-project-files',
  templateUrl: './project-files.component.html',
  styleUrls: ['./project-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProjectFilesComponent {
  @ViewChild('content') content!: TemplateRef<unknown>;

  readonly faCopy = faCopy;
  readonly faChevronDown = faChevronDown;
  readonly faChevronRight = faChevronRight;
  readonly samplePageSizes = [100, 250, 500];

  modalRef!: BsModalRef;
  projectFiles: ProjectFile[] = [];
  sampleColumns: SampleColumn[] = [];
  sampleRows: SampleRow[] = [];
  sampleFilesById: Record<string, SampleFilesState> = {};
  globalFilter = '';

  private projectRequestId = 0;

  get navConfig(): NavConfig {
    return PROJECT_FILES_NAV_CONFIG;
  }

  get globalFilterFields(): string[] {
    return this.sampleColumns.map((column) => column.data);
  }

  get initialRows(): number {
    return Math.min(100, this.sampleRows.length) || 10;
  }

  get rowsPerPageOptions(): { label: string; value: number }[] {
    const total = this.sampleRows.length;
    if (total === 0) {
      return [{ label: '10', value: 10 }];
    }

    const options = this.samplePageSizes
      .filter((size) => size < total)
      .map((size) => ({ label: String(size), value: size }));

    options.push({ label: 'All', value: total });
    return options;
  }

  constructor(
    private backendService: BackendService,
    private modalService: BsModalService,
    public projectService: ProjectService,
    private secureFileService: SecureFileService,
  ) {
    effect(() => {
      this.setproject();
    });
  }

  setproject(): void {
    const project = this.projectService.selectedProjectDetails();
    this.projectRequestId += 1;

    this.projectFiles = [];
    this.sampleColumns = [];
    this.sampleRows = [];
    this.sampleFilesById = {};
    this.globalFilter = '';

    if (project === null) {
      return;
    }

    this.sampleColumns = this.toSampleColumns(project.sample_cols);
    this.sampleRows = this.toSampleRows(project.samples);
    this.projectFiles = this.toProjectFiles(project);
  }

  copyLinkToClipboard(path: string): void {
    this.backendService.getS3Link(path).subscribe((response: any) => {
      if (response.status === 200) {
        navigator.clipboard.writeText(response.reply.link);
        this.modalRef = this.modalService.show(this.content);
      }
    });
  }

  onGlobalFilterChange(event: Event, table: Table): void {
    this.globalFilter = (event.target as HTMLInputElement).value;
    table.filterGlobal(this.globalFilter, FilterMatchMode.CONTAINS);
  }

  onSampleRowExpand(event: TableRowExpandEvent<SampleRow>): void {
    const sample = event.data;
    const existingState = this.sampleFilesById[sample.sample_id];
    if (existingState?.status === 'loading' || existingState?.status === 'loaded') {
      return;
    }

    const projectId = this.projectService.selectedProjectDetails()?.project_id;
    if (!projectId) {
      return;
    }

    const requestId = this.projectRequestId;
    this.setSampleFilesState(sample.sample_id, { status: 'loading' });

    this.backendService.getSampleFiles(projectId, sample.sample_id).subscribe({
      next: (response: unknown) => {
        if (requestId !== this.projectRequestId) {
          return;
        }

        if (!isRecord(response) || !isRecord(response['file_list'])) {
          this.setSampleFilesState(sample.sample_id, { status: 'error' });
          console.error('The sample files endpoint returned an invalid response.', response);
          return;
        }

        this.setSampleFilesState(sample.sample_id, {
          status: 'loaded',
          sections: this.toSampleFileSections(response['file_list']),
        });
      },
      error: (error: unknown) => {
        if (requestId !== this.projectRequestId) {
          return;
        }

        this.setSampleFilesState(sample.sample_id, { status: 'error' });
        console.error(`Failed to load files for sample ${sample.sample_id}.`, error);
      },
    });
  }

  getSampleFilesState(sample: SampleRow): SampleFilesState | undefined {
    return this.sampleFilesById[sample.sample_id];
  }

  downloadFile(filePath: string, filename?: string): void {
    this.secureFileService.downloadFile(filePath, { fileName: filename }).subscribe();
  }

  private toSampleColumns(value: unknown): SampleColumn[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const columns = value.filter(isSampleColumn);
    if (columns.length !== value.length) {
      console.error('Project sample columns contained invalid entries; invalid columns were omitted.');
    }
    return columns;
  }

  private toSampleRows(value: unknown): SampleRow[] {
    if (!Array.isArray(value) && !isRecord(value)) {
      return [];
    }

    const rows = Object.values(value).filter(isSampleRow);
    if (rows.length !== Object.values(value).length) {
      console.error('Project sample data contained invalid rows; rows without a sample ID were omitted.');
    }
    return rows;
  }

  private toProjectFiles(project: ProjectDetails): ProjectFile[] {
    if (!Array.isArray(project.files)) {
      return [];
    }

    return project.files.flatMap((file: unknown) => {
      if (!isRecord(file) || typeof file['filename'] !== 'string' || typeof file['url'] !== 'string') {
        return [];
      }

      const extension = typeof file['file_ext'] === 'string' ? file['file_ext'] : '';
      const fileIcon =
        extension in iconLookup ? iconLookup[extension as keyof typeof iconLookup] : faFileCircleQuestion;

      return [
        {
          file_name: file['filename'],
          file_path: file['url'],
          file_url: file['url'].replace('s3_redirect/', ''),
          file_icon: fileIcon,
        },
      ];
    });
  }

  private toSampleFileSections(fileList: Record<string, unknown>): SampleFileSection[] {
    const sections: SampleFileSection[] = [];
    this.appendFileSection('Entire sample', fileList, 0, sections);

    const loci = fileList['loci'];
    if (isRecord(loci)) {
      for (const [locus, node] of Object.entries(loci)) {
        this.appendFileSection(locus, node, 1, sections);
      }
    }

    return sections;
  }

  private appendFileSection(title: string, value: unknown, depth: number, sections: SampleFileSection[]): void {
    if (!isRecord(value)) {
      return;
    }

    if ('files' in value) {
      sections.push({
        title,
        depth,
        files: this.toSampleFiles(value['files']),
      });
    }

    const sublevel = value['sublevel'];
    if (isRecord(sublevel)) {
      for (const [name, child] of Object.entries(sublevel)) {
        this.appendFileSection(name, child, depth + 1, sections);
      }
    }
  }

  private toSampleFiles(value: unknown): SampleFile[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.flatMap((file: unknown) => {
      if (
        !Array.isArray(file) ||
        typeof file[0] !== 'string' ||
        typeof file[1] !== 'string' ||
        typeof file[2] !== 'string'
      ) {
        return [];
      }

      const extension = file[1].toLowerCase();
      const fileIcon =
        extension in iconLookup ? iconLookup[extension as keyof typeof iconLookup] : faFileCircleQuestion;

      return [
        {
          fileName: file[0],
          filePath: file[2],
          fileIcon,
        },
      ];
    });
  }

  private setSampleFilesState(sampleId: string, state: SampleFilesState): void {
    this.sampleFilesById = {
      ...this.sampleFilesById,
      [sampleId]: state,
    };
  }
}
